/**
 * The one detail panel shell (photo-preview-levels 02/03).
 *
 * Right drawer on tablet/desktop, bottom sheet on phones, with one header,
 * close control and scrollable content. The photo preview uses it now; the
 * search result pane and the item list preview adopt the same shell in ticket
 * 03, so a detail preview behaves identically across the app.
 */

import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { CloseIcon } from "./icons";
import { useT } from "../i18n";

export function DetailPanel({
  open,
  onClose,
  title,
  subtitle,
  children,
  testId,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  testId?: string;
}) {
  const t = useT();
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up("md"));
  return (
    <Drawer
      anchor={wide ? "right" : "bottom"}
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: wide ? 400 : "100%",
            maxHeight: wide ? "100%" : "82dvh",
            borderTopLeftRadius: { xs: 8, md: 0 },
            borderTopRightRadius: { xs: 8, md: 0 },
          },
        },
      }}
    >
      <Box data-testid={testId} sx={{ p: 1.5, overflowY: "auto" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap>
              {title}
            </Typography>
            {subtitle}
          </Box>
          <IconButton aria-label={t("close")} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        {children}
      </Box>
    </Drawer>
  );
}
